/**
 * Offline Data Cache - Stores reference data needed for offline timesheet creation
 */

const CACHE_KEY_PREFIX = '3pm-offline-data-';
const CACHE_VERSION = 1;
const CACHE_EXPIRY_DAYS = 7; // Cache expires after 7 days

interface CachedData<T> {
  data: T;
  timestamp: number;
  version: number;
}

class OfflineDataCache {
  /**
   * Check if cache is expired
   */
  private isExpired(timestamp: number): boolean {
    const expiryTime = CACHE_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
    return Date.now() - timestamp > expiryTime;
  }

  /**
   * Get cached data
   */
  get<T>(key: string): T | null {
    try {
      const cached = localStorage.getItem(`${CACHE_KEY_PREFIX}${key}`);
      if (!cached) return null;

      const parsed: CachedData<T> = JSON.parse(cached);
      
      // Check version
      if (parsed.version !== CACHE_VERSION) {
        localStorage.removeItem(`${CACHE_KEY_PREFIX}${key}`);
        return null;
      }

      // Check expiry
      if (this.isExpired(parsed.timestamp)) {
        localStorage.removeItem(`${CACHE_KEY_PREFIX}${key}`);
        return null;
      }

      return parsed.data;
    } catch (error) {
      console.error(`[OfflineDataCache] Error reading cache for ${key}:`, error);
      return null;
    }
  }

  /**
   * Set cached data
   */
  set<T>(key: string, data: T): void {
    try {
      const cached: CachedData<T> = {
        data,
        timestamp: Date.now(),
        version: CACHE_VERSION
      };
      localStorage.setItem(`${CACHE_KEY_PREFIX}${key}`, JSON.stringify(cached));
    } catch (error) {
      console.error(`[OfflineDataCache] Error writing cache for ${key}:`, error);
      // If quota exceeded, try to clear old caches
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        this.clearExpired();
        try {
          const cached: CachedData<T> = {
            data,
            timestamp: Date.now(),
            version: CACHE_VERSION
          };
          localStorage.setItem(`${CACHE_KEY_PREFIX}${key}`, JSON.stringify(cached));
        } catch (retryError) {
          console.error(`[OfflineDataCache] Failed to cache after cleanup:`, retryError);
        }
      }
    }
  }

  /**
   * Remove a specific cache entry by key
   */
  remove(key: string): void {
    try {
      localStorage.removeItem(`${CACHE_KEY_PREFIX}${key}`);
    } catch (error) {
      console.error(`[OfflineDataCache] Error removing cache for ${key}:`, error);
    }
  }

  /**
   * Clear expired caches
   */
  clearExpired(): void {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(CACHE_KEY_PREFIX)) {
          try {
            const cached = JSON.parse(localStorage.getItem(key) || '{}');
            if (this.isExpired(cached.timestamp) || cached.version !== CACHE_VERSION) {
              localStorage.removeItem(key);
            }
          } catch (e) {
            // Invalid cache entry, remove it
            localStorage.removeItem(key);
          }
        }
      });
    } catch (error) {
      console.error('[OfflineDataCache] Error clearing expired caches:', error);
    }
  }

  /**
   * Clear all cached data
   */
  clear(): void {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(CACHE_KEY_PREFIX)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error('[OfflineDataCache] Error clearing cache:', error);
    }
  }

  /**
   * Fetch data with offline fallback
   */
  async fetchWithCache<T>(
    key: string,
    fetchFn: () => Promise<T>,
    useCache: boolean = true
  ): Promise<T> {
    const isOnline = navigator.onLine;

    // If online, try to fetch fresh data
    if (isOnline) {
      try {
        const data = await fetchFn();
        // Cache the fresh data
        if (useCache) {
          this.set(key, data);
        }
        return data;
      } catch (error) {
        console.warn(`[OfflineDataCache] Fetch failed for ${key}, trying cache:`, error);
        // Fall back to cache if fetch fails
        const cached = this.get<T>(key);
        if (cached) {
          return cached;
        }
        throw error;
      }
    }

    // If offline, use cache
    const cached = this.get<T>(key);
    if (cached) {
      console.log(`[OfflineDataCache] Using cached data for ${key}`);
      return cached;
    }

    throw new Error(`No cached data available for ${key} and device is offline`);
  }
}

export const offlineDataCache = new OfflineDataCache();

// Cache keys
export const CACHE_KEYS = {
  PROJECTS: 'projects',
  EQUIPMENT: 'equipment',
  MATERIALS: 'materials',
  GENERAL_WORK_ITEMS: 'general-work-items',
  TASKS: (projectId: string) => `tasks-${projectId}`,
  ALL_TASKS: 'all-tasks',
  STAFF: 'staff',
  STAFF_GROUPS: 'staff-groups',
  TIMESHEETS: (date: string) => `timesheets-${date}`,
  TIMESHEETS_BY_STAFF: (date: string, staffIds: string[]) => `timesheets-${date}-${staffIds.sort().join(',')}`,
  ACCOUNT_SETTINGS: 'account-settings',
  ACCOUNT: 'account',
  CONTACT: (contactId: string) => `contact-${contactId}`,
  CONTACTS: 'contacts',
  CONTACTS_PAGE: (page: number, filterType: string, searchQuery: string) => `contacts-${page}-${filterType}-${searchQuery}`,
  USER_CUSTOM_QUICK_ADDRESSES: (userId: string) => `user-quick-addresses-${userId}`,
  SUBSCRIPTION: 'subscription',
  PAY_PERIOD: (offset: number) => `pay-period-${offset}`,
  LEAVE_TYPES: 'leave-types',
  LEAVE_REQUESTS: 'leave-requests',
  CLIENTS: 'clients',
  CONSTRUCTION_TYPES: 'construction-types'
};

